import { useEffect, useState } from "react";
import { api, money, dateIT } from "@/lib/api";
import { PageHeader, Btn, Field, Input, Select, Textarea, Modal, Badge } from "@/components/UI";
import { Plus, Trash, Receipt, FilePdf, Eye } from "@phosphor-icons/react";
import FilePreview from "@/components/FilePreview";

export default function SalPage() {
  const [items, setItems] = useState([]);
  const [filiere, setFiliere] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const [s, f, fatt] = await Promise.all([
      api.get("/sal"), api.get("/filiere"),
      api.get("/documenti"),
    ]);
    setItems(s.data);
    setFiliere(f.data);
    setFatture(fatt.data.filter((d) => d.tipo === "fattura"));
  };
  useEffect(() => { load(); }, []);

  const [form, setForm] = useState({
    filiera_id: "", numero: 1, data: new Date().toISOString().split("T")[0],
    fatture_ids: [], note: "",
  });

  const generate = async () => {
    if (!form.filiera_id || form.fatture_ids.length === 0) {
      alert("Seleziona filiera e almeno una fattura"); return;
    }
    try {
      await api.post("/sal/generate", form);
      setModal(null);
      setForm({ filiera_id: "", numero: 1, data: new Date().toISOString().split("T")[0], fatture_ids: [], note: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Confermi eliminazione?")) return;
    await api.delete(`/sal/${id}`);
    load();
  };

  const filiera_fatture = fatture.filter((f) => !form.filiera_id || f.filiera_id === form.filiera_id);
  const selectedTotal = fatture
    .filter((f) => form.fatture_ids.includes(f.id))
    .reduce((s, f) => s + (f.totale || 0), 0);

  return (
    <div className="fade-in" data-testid="sal-page">
      <PageHeader eyebrow="Rendicontazione" title="SAL & Dossier finali"
        actions={<Btn variant="primary" onClick={() => setModal(true)} data-testid="sal-new-btn">
          <Plus size={16} weight="bold" /> Genera nuovo SAL
        </Btn>} />

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">Nessun SAL generato</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>SAL n.</th><th>Data</th><th>Filiera</th>
                  <th className="text-right">Importo</th><th>Stato</th><th className="w-32">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const f = filiere.find((x) => x.id === s.filiera_id);
                  return (
                    <tr key={s.id} data-testid={`sal-row-${s.id}`}>
                      <td className="font-bold">#{s.numero}</td>
                      <td>{dateIT(s.data)}</td>
                      <td>{f?.nome || "-"}</td>
                      <td className="text-right font-medium">{money(s.importo)}</td>
                      <td><Badge tone={s.stato === "approvato" ? "green" : s.stato === "erogato" ? "blue" : "slate"}>{s.stato}</Badge></td>
                      <td>
                        <div className="flex items-center gap-1">
                          {s.pdf_file_id && (
                            <button onClick={() => setPreview({ id: s.pdf_file_id, filename: `SAL_${s.numero}.pdf`, ext: "pdf", contentType: "application/pdf" })}
                              className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-100 text-slate-700"
                              title="Visualizza PDF" data-testid={`sal-view-${s.id}`}>
                              <Eye size={15} />
                            </button>
                          )}
                          <button onClick={() => del(s.id)} className="w-8 h-8 rounded flex items-center justify-center hover:bg-red-50 text-red-600" data-testid={`sal-del-${s.id}`}>
                            <Trash size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title="Genera SAL da fatture" testId="sal-modal">
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Filiera *">
              <Select value={form.filiera_id} onChange={(e) => setForm({ ...form, filiera_id: e.target.value, fatture_ids: [] })} data-testid="sal-filiera">
                <option value="">--</option>
                {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </Select>
            </Field>
            <Field label="Numero SAL *">
              <Input type="number" value={form.numero} onChange={(e) => setForm({ ...form, numero: parseInt(e.target.value) || 1 })} data-testid="sal-numero" />
            </Field>
            <Field label="Data *">
              <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="sal-data" />
            </Field>
          </div>

          <div>
            <div className="eyebrow mb-2">Fatture da includere ({form.fatture_ids.length} selezionate · {money(selectedTotal)})</div>
            <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto">
              {filiera_fatture.length === 0 ? (
                <div className="p-4 text-slate-400 text-sm">Nessuna fattura disponibile</div>
              ) : filiera_fatture.map((f) => (
                <label key={f.id} className="flex items-center gap-3 p-2.5 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.fatture_ids.includes(f.id)}
                    onChange={(e) => {
                      const ids = e.target.checked
                        ? [...form.fatture_ids, f.id]
                        : form.fatture_ids.filter((x) => x !== f.id);
                      setForm({ ...form, fatture_ids: ids });
                    }}
                    data-testid={`sal-fatt-${f.id}`} />
                  <span className="mono text-xs w-24">{f.numero || "-"}</span>
                  <span className="text-xs text-slate-500 w-24">{dateIT(f.data)}</span>
                  <span className="flex-1 truncate">{f.oggetto || "-"}</span>
                  <span className="font-medium">{money(f.totale)}</span>
                </label>
              ))}
            </div>
          </div>

          <Field label="Note">
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="sal-note" />
          </Field>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Btn variant="secondary" onClick={() => setModal(null)}>Annulla</Btn>
            <Btn variant="primary" onClick={generate} data-testid="sal-generate-btn">
              <Receipt size={16} weight="bold" /> Genera SAL + PDF
            </Btn>
          </div>
        </div>
      </Modal>

      {preview && <FilePreview {...preview} fileId={preview.id} onClose={() => setPreview(null)} />}
    </div>
  );
}
